import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/auth';

export interface RecentActivity {
  id: string;
  moduleId: string;
  courseId: string;
  activityType: string;
  timestamp: string;
  timeSpent: number;
  metadata?: any;
  moduleName?: string;
  courseName?: string;
}

export const useRealTimeActivity = () => {
  const { user } = useSupabaseAuth();
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRecentActivities = async () => {
    console.log('📊 fetchRecentActivities called, user?.id:', user?.id);
    
    if (!user?.id) {
      console.log('📊 No user.id for activity fetch - waiting for auth');
      return; // Don't set loading false, wait for user
    }

    try {
      console.log('📊 Fetching activities for user:', user.id);
      setLoading(true);

      // Fetch recent activity logs
      const { data: activityLogs, error } = await supabase
        .from('module_activity_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('❌ Activity fetch error:', error);
        throw error;
      }
      
      console.log('📊 Fetched activities:', activityLogs?.length || 0, 'records');

      // If no activity logs, fetch recent course assignments as fallback
      if (!activityLogs || activityLogs.length === 0) {
        console.log('📦 No activity logs found, fetching course assignments as fallback...');
        
        const { data: assignments, error: assignmentError } = await supabase
          .from('course_assignments')
          .select('course_id, assigned_date, last_activity_date, status')
          .eq('user_id', user.id)
          .order('last_activity_date', { ascending: false, nullsFirst: false })
          .limit(5);

        if (assignmentError) {
          console.error('❌ Course assignment fetch error:', assignmentError);
        } else if (assignments && assignments.length > 0) {
          console.log('✅ Found', assignments.length, 'course assignments to display');
          
          const assignmentActivities: RecentActivity[] = assignments.map(assignment => ({
            id: `assignment-${assignment.course_id}`,
            moduleId: assignment.course_id,
            courseId: assignment.course_id,
            activityType: 'course_assigned',
            timestamp: assignment.last_activity_date || assignment.assigned_date,
            timeSpent: 0,
            metadata: { status: assignment.status },
            moduleName: assignment.course_id
              .replace(/-/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
            courseName: assignment.course_id
              .replace(/-/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          }));
          
          setActivities(assignmentActivities);
          setLoading(false);
          return;
        }
      }

      // SAFETY FILTER: Filter out disabled modules from activity display
      const DISABLED_MODULE_IDS = ['openai-api-mastery'];
      const filteredLogs = (activityLogs || []).filter(
        log => !DISABLED_MODULE_IDS.includes(log.module_id)
      );

      const processedActivities: RecentActivity[] = filteredLogs.map(log => ({
        id: log.id,
        moduleId: log.module_id,
        courseId: log.course_id,
        activityType: log.activity_type,
        timestamp: log.created_at,
        timeSpent: log.time_spent || 0,
        metadata: log.metadata,
        moduleName: getActivityTitle(log),
        courseName: getCourseTitle(log)
      }));

      setActivities(processedActivities);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActivityTitle = (log: any): string => {
    // Priority 1: Use metadata titles
    if (log.metadata?.module_title) {
      return log.metadata.module_title;
    }
    if (log.metadata?.course_title) {
      return log.metadata.course_title;
    }
    
    // Priority 2: Format IDs properly
    if (log.activity_type === 'course_access' || log.activity_type === 'course_overview_access') {
      // Use course_id and format it nicely
      return log.course_id
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    // For module activities, extract and format module name
    const moduleName = log.module_id
      .replace(log.course_id + '-module-', 'Module ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return moduleName;
  };

  const getCourseTitle = (log: any): string => {
    if (log.metadata?.course_title) {
      return log.metadata.course_title;
    }
    return log.course_id
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Mount-time fetch guarantee - handles deferred loading scenario
  useEffect(() => {
    console.log('📊 useRealTimeActivity mounted, user?.id:', user?.id);
    if (user?.id) {
      console.log('📊 User already available on mount, triggering fetch');
      fetchRecentActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Main effect for user changes and real-time subscription
  useEffect(() => {
    console.log('📊 useRealTimeActivity useEffect triggered, user?.id:', user?.id);
    
    if (!user?.id) {
      console.log('📊 No user.id, clearing activities');
      setActivities([]);
      setLoading(false);
      return;
    }

    // Fetch activities when user.id changes
    fetchRecentActivities();

    // Set up real-time subscription
    const channel = supabase
      .channel('activity_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'module_activity_logs',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📊 Real-time activity received:', payload.new);
          const newActivity: RecentActivity = {
            id: payload.new.id,
            moduleId: payload.new.module_id,
            courseId: payload.new.course_id,
            activityType: payload.new.activity_type,
            timestamp: payload.new.created_at,
            timeSpent: payload.new.time_spent || 0,
            metadata: payload.new.metadata,
            moduleName: getActivityTitle(payload.new),
            courseName: getCourseTitle(payload.new)
          };

          setActivities(prev => [newActivity, ...prev.slice(0, 9)]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]); // Use primitive user.id instead of user object

  return {
    activities,
    loading,
    refetch: fetchRecentActivities
  };
};